import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getProjectDetailsQueryKey } from '@/features/projects/shared/hooks/use-project-details';
import { getProjectsQueryKey } from '@/features/projects/shared/hooks/use-projects';
import { assignProjectPlan } from '@/features/subscriptions/api/assign-project-plan';
import { PROJECT_SUBSCRIPTION_QUERY_KEY } from '@/features/subscriptions/hooks/use-available-project-subscriptions';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import { PROJECT_ENTITLEMENTS_QUERY_KEY } from '@/features/subscriptions/hooks/use-project-entitlements';
import { useToast } from '@/shared/hooks/use-toast';
import { useErrorTranslation } from '@/shared/utils/error-handling';

export function useAssignProjectPlan() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const errorTranslation = useErrorTranslation();
  const { t } = useTranslation('subscriptions');
  const { openPaywallFromError } = usePaywall();

  return useMutation({
    mutationFn: assignProjectPlan,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PROJECT_SUBSCRIPTION_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: getProjectsQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getProjectDetailsQueryKey(String(variables.projectId)),
      });
      queryClient.invalidateQueries({
        queryKey: [PROJECT_ENTITLEMENTS_QUERY_KEY],
      });
      toast({
        title: t('projectPlans.assignmentSuccess'),
      });
    },
    onError: async (error) => {
      const handled = await openPaywallFromError(error);
      if (!handled) {
        toast({
          title: errorTranslation(error),
          variant: 'destructive',
        });
      }
    },
  });
}
